import { sansPrefixeNiveau } from '../../components/SelecteurContenu.tsx';
import type { ColonneSousDomaine, SequenceCellule } from '../../components/TableauProgressions.tsx';
import { categorie, compteurs, formaterDuree, type Activite, type Objectifs } from '../HeuresService.tsx';
import type { Dossier, Programme } from '../fiches/modele.ts';
import { htmlSequence, STYLE_FICHES } from '../fiches/impression.ts';
import { grilleEmploiDuTemps, STYLE_EDT } from '../../lib/edt-impression.ts';
import {
  BASE_V1, echapper, evaluationV1, heuresV1, programmationV1, progressionsV1, STYLE_EVALUATION_V1,
  STYLE_HEURES_V1, STYLE_JOURNAL_V1, STYLE_PROGRAMMATION_V1, STYLE_PROGRESSIONS_V1, tableJournalV1,
  titreV1, type DocumentV1,
} from '../../lib/gabarits-v1.ts';
import { decalerJour } from '../Journal.tsx';
import { lundiDe } from '../Progressions.tsx';

/**
 * Impressions — les sections du dossier.
 *
 * ── Les modèles des modules, et rien d'autre ────────────────────────────────
 * Chaque section est l'impression de son module, au modèle de la v1
 * (`lib/gabarits-v1.ts`) : le dossier et le bouton de l'écran sortent le même
 * document. Comme l'impression d'ensemble de la v1, chaque matière, chaque
 * période commence sur une page neuve, sous son propre titre.
 *
 * Seule la fiche de préparation garde son modèle à elle.
 *
 * Fonctions PURES : données de l'API en entrée, HTML échappé en sortie.
 */

// ── Données attendues ────────────────────────────────────────────────────────

export interface Periode {
  id: string;
  name: string;
  number: number;
  startsOn: string;
  endsOn: string;
}

export interface Matiere {
  id: string;
  name: string;
  color: string;
  areas: Array<{ id: string; name: string }>;
}

export interface Creneau {
  weekday: number;
  startsAt: string;
  endsAt: string;
  label: string;
  subjectName: string;
  subjectAreaText: string;
  color: string;
}

export interface Bloc {
  id: string;
  subjectId: string;
  curriculumDomainId: string | null;
  termId: string;
  content: string;
  position: number;
}

export interface Progression {
  id: string;
  termId: string | null;
  weekStart: string;
  subjectId: string;
  curriculumDomainId: string | null;
  sequence: string;
  content: string;
  competencies: string;
  done: boolean;
  position: number;
  reportedFromId: string | null;
}

export interface Seance {
  date: string;
  startsAt: string | null;
  endsAt: string | null;
  subjectId: string | null;
  subjectText: string;
  subjectAreaText: string;
  objective: string;
  procedure: string;
  materials: string;
  done: boolean;
  objectives: Array<{ text: string; done?: boolean }>;
}

export interface GrilleEvaluation {
  students: Array<{ id: string; lastName: string; firstName: string }>;
  domains: Array<{ name: string; color: string; assessments: Array<{ id: string; number: number; label: string }> }>;
  marks: Array<{ assessmentId: string; studentId: string; level: string }>;
  averages: Array<{ domainName: string; studentId: string; level: string | null; count: number }>;
}

export interface Eleve {
  lastName: string;
  firstName: string;
}

export interface Contexte {
  ecole: string;
  classe: string;
  annee: string;
  /** « Toute l'année » ou « Période 2 · du 2 novembre au 18 décembre 2026 ». */
  periode: string;
  enseignant: string;
  imprimeLe: string;
}

/**
 * Une section du dossier : une page neuve, sous son titre.
 *
 * `module` regroupe les sections au sommaire : douze matières de
 * programmation y font une ligne, pas douze.
 */
export interface Section {
  module: string;
  titre: string;
  html: string;
  paysage?: boolean;
  /** Faux pour la fiche de préparation, qui garde son propre style. */
  v1?: boolean;
}

/** « mardi 8 septembre 2026 ». Arithmétique en UTC, comme partout ailleurs. */
export function dateLongue(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}

const majuscule = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

const vide = (texte: string) => `<p class="ip-vide">${echapper(texte)}</p>`;

/** Une section depuis un modèle de module. */
const depuis = (module: string, doc: DocumentV1): Section =>
  ({ module, titre: doc.titre, html: doc.html, paysage: doc.orientation === 'paysage' });

/** Les sous-domaines d'une matière, groupés par nom, « Général » en dernier — comme à l'écran. */
function lignesDeLaMatiere(programme: Programme, matiere: string): ColonneSousDomaine[] {
  const parNom = new Map<string, string[]>();
  for (const d of programme) {
    if (d.subject !== matiere) continue;
    const nom = sansPrefixeNiveau(d.name);
    parNom.set(nom, [...(parNom.get(nom) ?? []), d.id]);
  }
  return [
    ...[...parNom].map(([nom, ids]) => ({ id: ids[0] as string | null, nom, ids })),
    { id: null, nom: 'Général', ids: [] as string[] },
  ];
}

// ── Page de garde ────────────────────────────────────────────────────────────

export function sectionGarde(ctx: Contexte, sommaire: string[]): string {
  const ligne = (libelle: string, valeur: string) => (valeur
    ? `<tr><th>${echapper(libelle)}</th><td>${echapper(valeur)}</td></tr>` : '');
  return `
    <section class="v1 ip-garde">
      <h1>Dossier de classe — ${echapper(ctx.classe)}</h1>
      <table class="ip-identite">
        ${ligne('École', ctx.ecole)}
        ${ligne('Enseignant', ctx.enseignant)}
        ${ligne('Année scolaire', ctx.annee)}
        ${ligne('Période', ctx.periode)}
        ${ligne('Imprimé le', ctx.imprimeLe)}
      </table>
      ${sommaire.length ? `<h2>Sommaire</h2>
        <ol class="ip-sommaire">${sommaire.map((t) => `<li>${echapper(t)}</li>`).join('')}</ol>` : ''}
    </section>`;
}

// ── Les modules ──────────────────────────────────────────────────────────────

export function sectionEleves(eleves: Eleve[], annee: string): Section[] {
  const html = eleves.length === 0 ? vide('Aucun élève dans la classe.') : `
    <table class="ip-eleves">
      <colgroup><col style="width:10%"><col style="width:45%"><col style="width:45%"></colgroup>
      <thead><tr><th>N°</th><th style="text-align:left">Nom</th><th style="text-align:left">Prénom</th></tr></thead>
      <tbody>${eleves.map((e, i) => `<tr><td style="text-align:center">${i + 1}</td>`
        + `<td>${echapper(e.lastName)}</td><td>${echapper(e.firstName)}</td></tr>`).join('')}</tbody>
    </table>`;
  return [{ module: 'Liste des élèves', titre: titreV1('Liste des élèves', annee), html }];
}

export function sectionEmploiDuTemps(creneaux: Creneau[], periode: string, annee: string): Section[] {
  return [{
    module: 'Emploi du temps',
    titre: titreV1('Emploi du Temps', periode, annee),
    html: grilleEmploiDuTemps(creneaux),
    paysage: true,
  }];
}

/** Une matière par page, sur toutes les périodes — comme l'impression d'ensemble de la v1. */
export function sectionProgrammation(
  blocs: Bloc[],
  matieres: Matiere[],
  periodes: Periode[],
  programme: Programme,
  semainesDe: (p: Periode) => number,
  annee: string,
): Section[] {
  return matieres.flatMap((m) => {
    const siens = blocs.filter((b) => b.subjectId === m.id).sort((a, b) => a.position - b.position);
    if (siens.length === 0) return [];
    return [depuis('Programmation annuelle', programmationV1(
      titreV1('Programmation', m.name, annee),
      m.color,
      periodes.map((p) => ({ id: p.id, nom: p.name || `Période ${p.number}`, semaines: semainesDe(p), debut: p.startsOn, fin: p.endsOn })),
      lignesDeLaMatiere(programme, m.name).map((l) => ({
        nom: l.nom,
        blocs: (periodeId: string) => siens
          .filter((b) => b.termId === periodeId
            && (l.id === null ? !b.curriculumDomainId || !programme.some((d) => d.id === b.curriculumDomainId && d.subject === m.name)
              : l.ids.includes(b.curriculumDomainId ?? '')))
          .map((b) => b.content),
      })),
    ))];
  });
}

/** Une matière et une période par page — comme l'impression d'ensemble de la v1. */
export function sectionProgressions(
  progressions: Progression[],
  matieres: Matiere[],
  periodes: Periode[],
  programme: Programme,
  annee: string,
): Section[] {
  return periodes.flatMap((p) => matieres.flatMap((m) => {
    const siennes = progressions.filter((x) => x.termId === p.id && x.subjectId === m.id);
    if (!siennes.some((x) => x.sequence.trim() || x.content.trim() || x.competencies.trim())) return [];
    const colonnes = lignesDeLaMatiere(programme, m.name);
    const connus = new Set(colonnes.flatMap((c) => c.ids));
    const sequences: SequenceCellule[] = siennes.map((x) => ({
      id: x.id,
      // Un domaine que la matière ne connaît plus va dans « Général », comme à l'écran.
      curriculumDomainId: x.curriculumDomainId && connus.has(x.curriculumDomainId) ? x.curriculumDomainId : null,
      weekStart: x.weekStart,
      sequence: x.sequence,
      competencies: x.competencies,
      content: x.content,
      done: x.done,
      reportedFromId: x.reportedFromId,
    }));
    const semaines = lundisEntre(p.startsOn, p.endsOn).map((lundi, i) => ({
      lundi, vendredi: decalerJour(lundi, 4), rang: i + 1,
    }));
    return [depuis('Progressions', progressionsV1(
      titreV1('Progressions', m.name, p.name || `Période ${p.number}`, annee),
      m.color, semaines, colonnes, sequences,
    ))];
  }));
}

/** Les lundis des semaines qui touchent l'intervalle, bornes comprises. */
export function lundisEntre(debut: string, fin: string): string[] {
  const lundis: string[] = [];
  for (let l = lundiDe(debut); l <= fin; l = decalerJour(l, 7)) lundis.push(l);
  return lundis;
}

/**
 * Les journées qui portent au moins une séance, au modèle de la v1 (A4
 * portrait). `unJourParPage` : une feuille par journée, comme l'écran.
 */
export function sectionJournal(
  seances: Seance[],
  matieres: Matiere[],
  unJourParPage: boolean,
  periode: string,
  annee: string,
): Section[] {
  const parId = new Map(matieres.map((m) => [m.id, m]));
  const jours = [...new Set(seances.map((s) => s.date))].sort();
  const html = jours.map((jour, i) => {
    const lignes = seances
      .filter((s) => s.date === jour)
      .sort((a, b) => (a.startsAt ?? '99').localeCompare(b.startsAt ?? '99'))
      .map((s) => {
        const matiere = s.subjectId ? parId.get(s.subjectId) : undefined;
        const objectifs = s.objectives.length
          ? s.objectives.map((o) => ({ texte: o.text, fait: !!o.done }))
          : (s.objective ? [{ texte: s.objective }] : []);
        return {
          debut: s.startsAt,
          fin: s.endsAt,
          domaine: s.subjectText || matiere?.name || '',
          sousDomaine: s.subjectAreaText,
          couleur: matiere?.color ?? '#94a3b8',
          objectifs,
          deroulement: s.procedure,
          materiel: s.materials,
        };
      });
    return `<div class="${unJourParPage && i > 0 ? 'v1-saut' : ''}">
      <h2>${echapper(majuscule(dateLongue(jour)))}</h2>${tableJournalV1(lignes)}</div>`;
  }).join('');
  return [{
    module: 'Cahier journal',
    titre: titreV1('Cahier journal', periode, annee),
    html: html || vide('Aucune séance consignée sur cette période.'),
  }];
}

export function sectionFiches(dossiers: Dossier[]): Section[] {
  return [{
    module: 'Fiches de préparation',
    titre: 'Fiches de préparation',
    html: dossiers.length ? dossiers.map(htmlSequence).join('') : vide('Aucune fiche de préparation.'),
    v1: false,
  }];
}

/** Une matière par page. */
export function sectionEvaluation(
  grilles: Array<{ matiere: string; grille: GrilleEvaluation }>,
  annee: string,
): Section[] {
  return grilles
    .filter(({ grille }) => grille.domains.some((d) => d.assessments.length > 0))
    .map(({ matiere, grille }) => depuis('Évaluation', evaluationV1(titreV1('Évaluation', matiere, annee), grille)));
}

/** Le document des 108 heures, au modèle de la v1 : partagé par l'écran et le dossier. */
export function documentHeuresV1(titre: string, activites: Activite[], objectifs: Objectifs): DocumentV1 {
  const bilan = compteurs(activites, objectifs);
  return heuresV1(
    titre,
    bilan.postes.map((p) => ({ nom: p.nom, fait: p.fait, cible: p.cible })),
    { fait: bilan.total, cible: bilan.cibleTotal },
    [...activites]
      .sort((a, b) => a.date.localeCompare(b.date) || a.category.localeCompare(b.category))
      .map((a) => ({ date: a.date, categorie: categorie(a.category).nom, minutes: a.minutes, intitule: a.title })),
    formaterDuree,
  );
}

export function sectionHeures(activites: Activite[], objectifs: Objectifs, annee: string): Section[] {
  return [depuis('108 heures', documentHeuresV1(titreV1('108 heures', annee ? `Année ${annee}` : ''), activites, objectifs))];
}

// ── Le dossier ───────────────────────────────────────────────────────────────

/**
 * Assemble la garde et les sections : chacune sur une page neuve, sous son
 * titre, dans l'orientation de son module — paysage par une PAGE NOMMÉE.
 */
export function assemblerDossier(ctx: Contexte, sections: Section[], avecGarde: boolean): string {
  const sommaire = [...new Set(sections.map((s) => s.module))];
  return (avecGarde ? sectionGarde(ctx, sommaire) : '')
    + sections.map((s, i) => `
      <section class="${s.v1 === false ? '' : 'v1 '}ip-section${s.paysage ? ' ip-paysage' : ''}${avecGarde || i > 0 ? ' ip-nouvelle-page' : ''}">
        ${s.v1 === false ? `<h1 class="ip-titre-fiches">${echapper(s.titre)}</h1>` : `<h1>${echapper(s.titre)}</h1>`}
        ${s.html}
      </section>`).join('');
}

/** Les styles du dossier : la base v1, ceux de chaque module, et la fiche. */
export const STYLE_DOSSIER = `
  @page paysage { size: A4 landscape; margin: 10mm; }
  .ip-paysage { page: paysage; }
  .ip-nouvelle-page { break-before: page; }
  .ip-vide { color: #777; font-style: italic; }
  ${BASE_V1}
  .ip-garde table.ip-identite { width: auto; table-layout: auto; margin: 4mm 0 8mm; }
  .ip-garde .ip-identite th, .ip-garde .ip-identite td { border: none; text-align: left; font-size: 10pt; padding: 1mm 6mm 1mm 0; }
  .ip-garde .ip-identite th { color: #555; font-weight: 400; }
  .ip-sommaire { margin: 0 0 0 6mm; list-style: decimal; font-size: 10.5pt; }
  .ip-sommaire li { margin-bottom: 2mm; }
  .ip-eleves thead th { background: #2d3748; color: white; }
  .ip-eleves tbody tr:nth-child(even) td { background: #fafafa; }
  .ip-titre-fiches { font-size: 13pt; font-weight: 700; margin-bottom: 5mm; }
  ${STYLE_EDT}
  ${STYLE_JOURNAL_V1}
  ${STYLE_PROGRESSIONS_V1}
  ${STYLE_PROGRAMMATION_V1}
  ${STYLE_EVALUATION_V1}
  ${STYLE_HEURES_V1}
  ${STYLE_FICHES}
`;
